import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';

interface MinimapElement {
  svgType: 'polygon' | 'rect' | 'scenary' | 'ellipse' | 'line';
  points?: string;
  pathD?: string;
  x?: number;
  y?: number;
  cx?: number;
  cy?: number;
  rx?: number;
  ry?: number;
  width?: number;
  height?: number;
  transform?: string;
  fill: string;
  stroke: string;
  strokeWidth: number;
  opacity: number;
  isZone: boolean;
  isSelected: boolean;
  isScenary: boolean;
  zone?: { levels: any[]; shapes: string[]; name: string | null };
  label?: string | null;
  labelX?: number;
  labelY?: number;
  fontSize?: number;
}

interface ViewState { x: number; y: number; w: number; h: number; }

@Component({
  selector: 'app-seat-map-minimap',
  templateUrl: './seat-map-minimap.component.html',
  styleUrls: ['./seat-map-minimap.component.scss']
})
export class SeatMapMinimapComponent implements OnChanges {
  @Input() navPreview: any[] = [];
  @Input() selectedZone: any = null;
  @Output() zoneSelected = new EventEmitter<any>();

  collapsed = false;
  svgElements: MinimapElement[] = [];

  readonly MINIMAP_W = 200;
  readonly MINIMAP_H = 150;
  readonly PADDING = 8;

  viewState: ViewState = { x: 0, y: 0, w: this.MINIMAP_W, h: this.MINIMAP_H };

  isDragging = false;
  private dragMoved = false;
  private dragStart = { cx: 0, cy: 0, vx: 0, vy: 0 };

  get isZoomed(): boolean { return this.viewState.w < this.MINIMAP_W - 1; }

  get svgViewBoxDynamic(): string {
    const { x, y, w, h } = this.viewState;
    return `${x} ${y} ${w} ${h}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['navPreview'] || changes['selectedZone']) {
      this.buildSvgElements();
    }
  }

  // Devuelve los 3 puntos del Bézier de una línea (inicio, control, fin) en coords locales.
  // El control define la curvatura; con curvature 0 cae en el punto medio (recta).
  private lineHullPoints(cfg: any): Array<{ x: number; y: number }> {
    const pts: number[] = cfg.points || [];
    if (pts.length < 4) return [];
    const [x1, y1, x2, y2] = pts;
    const curvature = cfg.curvature || 0;
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const qx = mx + (-dy / len) * curvature;
    const qy = my + (dx / len) * curvature;
    return [{ x: x1, y: y1 }, { x: qx, y: qy }, { x: x2, y: y2 }];
  }

  private buildSvgElements(): void {
    if (!this.navPreview?.length) {
      this.svgElements = [];
      return;
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    this.navPreview.forEach((obj: any) => {
      const type = obj.type;
      const cfg = obj.configuration || {};
      const cx = cfg.left || 0;
      const cy = cfg.top || 0;
      const sx = cfg.scaleX || 1;
      const sy = cfg.scaleY || 1;
      const rad = ((cfg.angle || 0) * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);

      if (type === 'polygon' && Array.isArray(cfg.points) && cfg.points.length > 0) {
        const xs = cfg.points.map((p: any) => p.x);
        const ys = cfg.points.map((p: any) => p.y);
        const poX = (Math.min(...xs) + Math.max(...xs)) / 2;
        const poY = (Math.min(...ys) + Math.max(...ys)) / 2;
        cfg.points.forEach((p: any) => {
          const lx = (p.x - poX) * sx;
          const ly = (p.y - poY) * sy;
          const wx = cx + lx * cos - ly * sin;
          const wy = cy + lx * sin + ly * cos;
          minX = Math.min(minX, wx); maxX = Math.max(maxX, wx);
          minY = Math.min(minY, wy); maxY = Math.max(maxY, wy);
        });
      } else if (type === 'ellipse') {
        // El bounding box de la elipse es ±rx, ±ry desde el centro (rx/ry ya son radios).
        const hw = (cfg.rx || 150) * sx;
        const hh = (cfg.ry || 150) * sy;
        minX = Math.min(minX, cx - hw); maxX = Math.max(maxX, cx + hw);
        minY = Math.min(minY, cy - hh); maxY = Math.max(maxY, cy + hh);
      } else if (type === 'line') {
        const lp = this.lineHullPoints(cfg);
        if (lp.length > 0) {
          const poX = (Math.min(...lp.map(p => p.x)) + Math.max(...lp.map(p => p.x))) / 2;
          const poY = (Math.min(...lp.map(p => p.y)) + Math.max(...lp.map(p => p.y))) / 2;
          lp.forEach(p => {
            const lx = (p.x - poX) * sx;
            const ly = (p.y - poY) * sy;
            const wx = cx + lx * cos - ly * sin;
            const wy = cy + lx * sin + ly * cos;
            minX = Math.min(minX, wx); maxX = Math.max(maxX, wx);
            minY = Math.min(minY, wy); maxY = Math.max(maxY, wy);
          });
        }
      } else {
        const hw = ((cfg.width || 300) * sx) / 2;
        const hh = ((cfg.height || 150) * sy) / 2;
        minX = Math.min(minX, cx - hw); maxX = Math.max(maxX, cx + hw);
        minY = Math.min(minY, cy - hh); maxY = Math.max(maxY, cy + hh);
      }
    });

    const pad = this.PADDING;
    const worldW = Math.max(maxX - minX, 1);
    const worldH = Math.max(maxY - minY, 1);
    const svgW = this.MINIMAP_W - 2 * pad;
    const svgH = this.MINIMAP_H - 2 * pad;
    const scale = Math.min(svgW / worldW, svgH / worldH);

    const offsetX = pad + (svgW - worldW * scale) / 2;
    const offsetY = pad + (svgH - worldH * scale) / 2;

    const toSvgX = (wx: number) => (wx - minX) * scale + offsetX;
    const toSvgY = (wy: number) => (wy - minY) * scale + offsetY;

    const selectedLevels = new Set(
      (this.selectedZone?.levels || []).map((l: any) => String(l))
    );
    const selectedShapes = new Set(
      (this.selectedZone?.shapes || []).map((s: any) => String(s))
    );

    const elements: MinimapElement[] = [];

    this.navPreview.forEach((obj: any) => {
      const type = obj.type;
      const cfg = obj.configuration || {};
      const levels: any[] = Array.isArray(cfg.levels) ? cfg.levels : [];
      const shapes: string[] = Array.isArray(cfg.navZoneShapes) ? cfg.navZoneShapes : [];
      const isZone = levels.length > 0 || shapes.length > 0;
      const isSelected = isZone && (
        levels.length === selectedLevels.size && levels.every((l: any) => selectedLevels.has(String(l)))
        && shapes.length === selectedShapes.size && shapes.every((s: any) => selectedShapes.has(String(s)))
      );

      const angle = cfg.angle || 0;
      const sx = cfg.scaleX || 1;
      const sy = cfg.scaleY || 1;
      const cx = cfg.left || 0;
      const cy = cfg.top || 0;
      const cxSvg = toSvgX(cx);
      const cySvg = toSvgY(cy);
      const baseStrokeW = Math.max(0.5 * scale, 0.4);

      if (type === 'polygon') {
        const pts: Array<{ x: number; y: number }> = cfg.points || [];
        if (pts.length < 2) return;

        const xs = pts.map((p: any) => p.x);
        const ys = pts.map((p: any) => p.y);
        const poX = (Math.min(...xs) + Math.max(...xs)) / 2;
        const poY = (Math.min(...ys) + Math.max(...ys)) / 2;
        const rad = (angle * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);

        const svgPoints = pts.map((p: any) => {
          const lx = (p.x - poX) * sx;
          const ly = (p.y - poY) * sy;
          const wx = cx + (lx * cos - ly * sin);
          const wy = cy + (lx * sin + ly * cos);
          return `${toSvgX(wx)},${toSvgY(wy)}`;
        }).join(' ');

        elements.push({
          svgType: 'polygon', points: svgPoints,
          fill: cfg.fill || '#d8e1d7',
          stroke: cfg.stroke || '#a9a8b3',
          strokeWidth: isSelected ? baseStrokeW * 2.5 : baseStrokeW,
          opacity: isSelected ? 1 : (isZone ? 0.55 : 0.8),
          isZone, isSelected, isScenary: false,
          zone: isZone ? { levels, shapes, name: cfg.name ?? null } : undefined,
          label: cfg.name || null, labelX: cxSvg, labelY: cySvg,
          fontSize: Math.max((cfg.labelFontSize || 40) * scale * 0.45, 7)
        });

      } else if (type === 'rect') {
        const w = (cfg.width || 300) * sx * scale;
        const h = (cfg.height || 150) * sy * scale;
        elements.push({
          svgType: 'rect', x: cxSvg - w / 2, y: cySvg - h / 2, width: w, height: h,
          transform: angle ? `rotate(${angle},${cxSvg},${cySvg})` : undefined,
          fill: cfg.fill || '#d8e1d7',
          stroke: cfg.stroke || '#a9a8b3',
          strokeWidth: isSelected ? baseStrokeW * 2.5 : baseStrokeW,
          opacity: isSelected ? 1 : (isZone ? 0.55 : 0.8),
          isZone, isSelected, isScenary: false,
          zone: isZone ? { levels, shapes, name: cfg.name ?? null } : undefined,
          label: cfg.name || null, labelX: cxSvg, labelY: cySvg,
          fontSize: Math.max((cfg.labelFontSize || 40) * scale * 0.45, 7)
        });

      } else if (type === 'ellipse') {
        const rx = (cfg.rx || 150) * sx * scale;
        const ry = (cfg.ry || 150) * sy * scale;
        elements.push({
          svgType: 'ellipse', cx: cxSvg, cy: cySvg, rx, ry,
          transform: angle ? `rotate(${angle},${cxSvg},${cySvg})` : undefined,
          fill: cfg.fill || '#d8e1d7',
          stroke: cfg.stroke || '#a9a8b3',
          strokeWidth: isSelected ? baseStrokeW * 2.5 : baseStrokeW,
          opacity: isSelected ? 1 : (isZone ? 0.55 : 0.8),
          isZone, isSelected, isScenary: false,
          zone: isZone ? { levels, shapes, name: cfg.name ?? null } : undefined,
          label: cfg.name || null, labelX: cxSvg, labelY: cySvg,
          fontSize: Math.max((cfg.labelFontSize || 40) * scale * 0.45, 7)
        });

      } else if (type === 'line') {
        const lp = this.lineHullPoints(cfg);
        if (lp.length < 3) return;
        const poX = (Math.min(...lp.map(p => p.x)) + Math.max(...lp.map(p => p.x))) / 2;
        const poY = (Math.min(...lp.map(p => p.y)) + Math.max(...lp.map(p => p.y))) / 2;
        const rad = (angle * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const toW = (p: { x: number; y: number }) => {
          const lx = (p.x - poX) * sx;
          const ly = (p.y - poY) * sy;
          return { x: toSvgX(cx + (lx * cos - ly * sin)), y: toSvgY(cy + (lx * sin + ly * cos)) };
        };
        const e0 = toW(lp[0]);
        const ec = toW(lp[1]);
        const e1 = toW(lp[2]);
        elements.push({
          svgType: 'line',
          pathD: `M ${e0.x} ${e0.y} Q ${ec.x} ${ec.y} ${e1.x} ${e1.y}`,
          fill: 'none',
          stroke: cfg.stroke || '#000000',
          strokeWidth: Math.max((cfg.strokeWidth || 3) * scale, 0.5),
          opacity: 0.8,
          isZone: false, isSelected: false, isScenary: false,
          label: null, labelX: cxSvg, labelY: cySvg, fontSize: 7
        });

      } else if (type === 'scenary') {
        const w = (cfg.width || 700) * sx * scale;
        const h = (cfg.height || 250) * sy * scale;
        elements.push({
          svgType: 'scenary', x: cxSvg - w / 2, y: cySvg - h / 2, width: w, height: h,
          transform: angle ? `rotate(${angle},${cxSvg},${cySvg})` : undefined,
          fill: cfg.fill || '#cce8f4', stroke: '#7ab8cc',
          strokeWidth: Math.max(0.5 * scale, 0.4),
          opacity: 1, isZone: false, isSelected: false, isScenary: true,
          label: cfg.text || 'Escenario', labelX: cxSvg, labelY: cySvg,
          fontSize: Math.max(10 * scale, 7)
        });
      }
    });

    elements.sort((a, b) => {
      const rank = (el: MinimapElement) =>
        el.isScenary ? 0 : (!el.isZone ? 1 : (!el.isSelected ? 2 : 3));
      return rank(a) - rank(b);
    });

    this.svgElements = elements;
  }

  // ── Zoom + pan ──────────────────────────────────────────────────────────────

  onWheel(event: WheelEvent, svgEl: HTMLElement): void {
    event.preventDefault();
    const factor = event.deltaY < 0 ? 0.75 : 1 / 0.75;
    const rect = svgEl.getBoundingClientRect();
    const mx = event.clientX - rect.left;
    const my = event.clientY - rect.top;

    // Mouse position in SVG world coords
    const svgX = this.viewState.x + (mx / this.MINIMAP_W) * this.viewState.w;
    const svgY = this.viewState.y + (my / this.MINIMAP_H) * this.viewState.h;

    const newW = Math.min(Math.max(this.viewState.w * factor, this.MINIMAP_W / 6), this.MINIMAP_W);
    const newH = Math.min(Math.max(this.viewState.h * factor, this.MINIMAP_H / 6), this.MINIMAP_H);

    // Keep the point under the mouse fixed
    const newX = svgX - (mx / this.MINIMAP_W) * newW;
    const newY = svgY - (my / this.MINIMAP_H) * newH;

    this.viewState = this.clampView({ x: newX, y: newY, w: newW, h: newH });
  }

  onMouseDown(event: MouseEvent): void {
    if (event.button !== 0) return;
    this.isDragging = true;
    this.dragMoved = false;
    this.dragStart = { cx: event.clientX, cy: event.clientY, vx: this.viewState.x, vy: this.viewState.y };
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    const dx = event.clientX - this.dragStart.cx;
    const dy = event.clientY - this.dragStart.cy;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.dragMoved = true;
    const ddx = (dx / this.MINIMAP_W) * this.viewState.w;
    const ddy = (dy / this.MINIMAP_H) * this.viewState.h;
    this.viewState = this.clampView({
      ...this.viewState,
      x: this.dragStart.vx - ddx,
      y: this.dragStart.vy - ddy
    });
  }

  onMouseUp(): void { this.isDragging = false; }
  onMouseLeave(): void { this.isDragging = false; }

  resetZoom(): void {
    this.viewState = { x: 0, y: 0, w: this.MINIMAP_W, h: this.MINIMAP_H };
  }

  private clampView(vs: ViewState): ViewState {
    return {
      x: Math.max(0, Math.min(vs.x, this.MINIMAP_W - vs.w)),
      y: Math.max(0, Math.min(vs.y, this.MINIMAP_H - vs.h)),
      w: vs.w, h: vs.h
    };
  }

  // ── Interactions ────────────────────────────────────────────────────────────

  onZoneClick(el: MinimapElement): void {
    if (this.dragMoved) return;  // ignore click after drag
    if (!el.isZone || el.isSelected || !el.zone) return;
    this.zoneSelected.emit(el.zone);
  }

  toggleCollapse(): void { this.collapsed = !this.collapsed; }
}
